import React, { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import { Servers } from '../../utils/api/servers'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import { axiosFetcher } from '../../utils/api/axios-fetcher'
import clsx from 'clsx'
import { Box, Chip, Divider, Stack, Typography } from '@mui/material'
import PowerIcon from '@mui/icons-material/Power'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import {
  DataGridPro, type GridAutosizeOptions, type GridColDef, type GridColumnGroupingModel, type GridSortModel, useGridApiRef,
} from '@mui/x-data-grid-pro'
import { type GridRowModelUpdate } from '@mui/x-data-grid/models/gridRows'
import { formatPercentage, formatRoundedUsdAmount, formatUsdPrice } from '../../utils/rounding-formatting-utils'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../utils/time-utils'
import {
  type BalancePlusDataInitial, type BalancePlusDataInitialItem, type BalancePlusDataStrategy,
} from '../../models/balance-plus/balance-plus-types'
import StackColumnLayout from '../../layouts/stack-column-layout'
import { useHeartbeatsImmutable } from '../../hooks/data/use-heartbeats'
import { isEmptyString } from '../../utils/string-utils'

export interface BalancePlusDataGridRow extends GridRowModelUpdate {
  coin: string
  balance: string
  curr_funding_bp: string
  curr_funding_apr: string
  seven_day_hist_funding_bp: string
  seven_day_hist_funding_apr: string
  tier: string
  position_limit: string
  open_interest: string
  width: string
  uneven: string
  firmwidePricingConfig?: Record<string, string>
}

interface BalancePlusDataGridProps {
  portfolioId: string
}

const COIN_ORDER = ['USDC', 'USDT']

const coinSorter = (a: BalancePlusDataInitialItem, b: BalancePlusDataInitialItem): number => {
  if (COIN_ORDER.indexOf(a.coin) > COIN_ORDER.indexOf(b.coin)) {
    return 1
  }
  if (COIN_ORDER.indexOf(b.coin) > COIN_ORDER.indexOf(a.coin)) {
    return -1
  }
  return a.coin.localeCompare(b.coin)
}

const COLUMNS: GridColDef[] = [
  { field: 'coin', headerName: 'Coin', type: 'string', minWidth: 200 },
  {
    field: 'balance',
    headerName: 'Balance',
    type: 'number',
    minWidth: 120,
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      return clsx('text', {
        negative: Number(params.value) > Number(params.row.position_limit),
      })
    },
  },
  {
    field: 'curr_funding_bp',
    headerName: 'bp',
    type: 'number',
    minWidth: 80,
  },
  {
    field: 'curr_funding_apr',
    headerName: 'APR',
    type: 'number',
    minWidth: 120,
    valueFormatter: (value) => formatPercentage(value),
  },
  {
    field: 'seven_day_hist_funding_bp',
    headerName: 'bp',
    type: 'number',
    minWidth: 80,
  },
  {
    field: 'seven_day_hist_funding_apr',
    headerName: 'APR',
    type: 'number',
    minWidth: 120,
    valueFormatter: (value) => formatPercentage(value),
  },
  {
    field: 'tier',
    headerName: 'Tier',
    type: 'string',
    minWidth: 50,
  },
  {
    field: 'position_limit',
    headerName: 'Position Limit',
    type: 'number',
    minWidth: 120,
    valueFormatter: (value) => formatRoundedUsdAmount(value),
  },
  {
    field: 'open_interest',
    headerName: 'Open Interest',
    type: 'number',
    minWidth: 120,
    valueFormatter: (value) => formatRoundedUsdAmount(value),
  },
  {
    field: 'width',
    headerName: 'Width',
    type: 'number',
    minWidth: 80,
    valueFormatter: (value) => formatUsdPrice(value),
  },
  {
    field: 'uneven',
    headerName: 'Uneven',
    type: 'string',
    minWidth: 80,
  },
  {
    field: 'firm_spread_bid',
    headerName: 'Bid',
    type: 'number',
    minWidth: 80,
    valueGetter: (_value, row, _column, _apiRef) => row.firmwidePricingConfig.firm_spread_bid_bps,
    valueFormatter: (value) => formatUsdPrice(value),
  },
  {
    field: 'firm_spread_ask',
    headerName: 'Ask',
    type: 'number',
    minWidth: 80,
    valueGetter: (_value, row, _column, _apiRef) => row.firmwidePricingConfig.firm_spread_ask_bps,
    valueFormatter: (value) => formatUsdPrice(value),
  },
]

const COLUMN_GROUPING_MODEL: GridColumnGroupingModel = [
  {
    groupId: 'curr_funding',
    headerName: 'Curr. Funding',
    description: 'Current Funding',
    headerAlign: 'center',
    children: [
      { field: 'curr_funding_bp' }, { field: 'curr_funding_apr' },
    ],
  },
  {
    groupId: 'seven_day_hist_funding',
    headerName: '7D Hist. Funding',
    description: '7D Historical Funding',
    headerAlign: 'center',
    children: [
      { field: 'seven_day_hist_funding_bp' }, { field: 'seven_day_hist_funding_apr' },
    ],
  },
  {
    groupId: 'firm_spread',
    headerName: 'Firm Spread (bps)',
    description: 'Firm Spread (bps)',
    headerAlign: 'center',
    children: [
      { field: 'firm_spread_bid' }, { field: 'firm_spread_ask' },
    ],
  },
]

const AUTOSIZE_OPTIONS: GridAutosizeOptions = {
  columns: Array.from(COLUMNS, item => item?.field),
  includeHeaders: true,
  includeOutliers: true,
}

const BalancePlusDataGrid: React.FC<BalancePlusDataGridProps> = (props) => {
  const { portfolioId } = props

  const apiRef = useGridApiRef()
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([])

  const { data, error } = useSWR<BalancePlusDataInitial, boolean>(
    `https://www.newtypecrypto.com/${portfolioId.toLowerCase()}/balanceplus`,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000,
    },
  )

  const { isAuthorized, accessToken } = useContext(AuthContext)
  const { data: dataStrategy, error: errorStrategy } = useSWR<BalancePlusDataStrategy, boolean>(
    isAuthorized ? [Servers.STRATEGY_GATEWAY, null, 'strategy-management/balance-plus', accessToken, { portfolioId }] : null,
    axiosFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000,
    },
  )

  const [strategyIdToNameMapping, setStrategyIdToNameMapping] = useState<Record<string, string>>({})
  const heartbeats = useHeartbeatsImmutable(isAuthorized, accessToken)
  useEffect(() => {
    const newMapping: Record<string, string> = {}
    for (const heartbeat of heartbeats) {
      const strategyId = heartbeat.strategyId
      const strategyName = heartbeat.strategyName
      newMapping[strategyId] = !isEmptyString(strategyName) ? `${strategyName} (${strategyId})` : strategyId
    }
    setStrategyIdToNameMapping(newMapping)
  }, [heartbeats])

  const [flaskStatus, setFlaskStatus] = useState<boolean>(false)
  const [gatewayStatus, setGatewayStatus] = useState<boolean>(false)
  const [timestamp, setTimestamp] = useState<string>('')
  const [rows, setRows] = useState<BalancePlusDataGridRow[]>([])

  useEffect(() => {
    const flaskConnectionOnline = data != null
    const gatewayConnectionOnline = dataStrategy != null
    setFlaskStatus(flaskConnectionOnline)
    setGatewayStatus(gatewayConnectionOnline)

    if (error === true || (!flaskConnectionOnline && !gatewayConnectionOnline)) {
      setTimestamp('')
      setRows([])
    } else {
      const combined: BalancePlusDataGridRow[] = []

      if (flaskConnectionOnline && data.data.length > 0) {
        data.data
          .sort(coinSorter)
          .forEach(item => {
            const coin = item.coin

            if (gatewayConnectionOnline) {
              const matchingCoinItems = dataStrategy
                .filter(dataStrategyItem => coin === dataStrategyItem.strategyCoin)

              if (matchingCoinItems.length > 0) {
                if (matchingCoinItems.length === 1) {
                  combined.push(
                    {
                      ...item,
                      position_limit: matchingCoinItems[0].positionLimit,
                      width: matchingCoinItems[0].globalSpread,
                      uneven: '',
                    }
                  )
                } else {
                  for (const matchingCoinItem of matchingCoinItems) {
                    combined.push(
                      {
                        ...item,
                        coin: `${coin} (${strategyIdToNameMapping[matchingCoinItem.strategyId]})`,
                        position_limit: matchingCoinItem.positionLimit,
                        width: matchingCoinItem.globalSpread,
                        uneven: '',
                      }
                    )
                  }
                }
              } else {
                combined.push(
                  {
                    ...item,
                    position_limit: '-',
                    width: '-',
                    uneven: '',
                  }
                )
              }
            } else {
              combined.push(
                {
                  ...item,
                  position_limit: '-',
                  width: '-',
                  uneven: '',
                }
              )
            }
          })
        setTimestamp(formatToLocalMilliTimestampFromUTCNumber(data.timestamp))
      } else if (gatewayConnectionOnline) {
        const uniqueCoins = new Set(dataStrategy.map(item => item.strategyCoin))
        Array.from(uniqueCoins)
          .sort((a, b) => {
            if (COIN_ORDER.indexOf(a) > COIN_ORDER.indexOf(b)) {
              return 1
            }
            if (COIN_ORDER.indexOf(b) > COIN_ORDER.indexOf(a)) {
              return -1
            }
            return a.localeCompare(b)
          })
          .forEach(uniqueCoin => {
            // Find any duplicates and de-dupe by specifying both the strategyCoin and strategyId as the coin property
            const matchingCoinItems = dataStrategy
              .filter(dataStrategyItem => uniqueCoin === dataStrategyItem.strategyCoin)
              .sort((a, b) => a.strategyId.localeCompare(b.strategyId, undefined, { numeric: true }))

            if (matchingCoinItems.length === 1) {
              combined.push({
                coin: uniqueCoin,
                balance: '-',
                curr_funding_bp: '-',
                curr_funding_apr: '-',
                seven_day_hist_funding_bp: '-',
                seven_day_hist_funding_apr: '-',
                tier: '-',
                position_limit: matchingCoinItems[0].positionLimit,
                open_interest: '-',
                width: matchingCoinItems[0].globalSpread,
                uneven: '-',
                firmwidePricingConfig: matchingCoinItems[0].firmwidePricingConfig
              })
            } else {
              for (const matchingCoinItem of matchingCoinItems) {
                combined.push({
                  coin: `${uniqueCoin} (${strategyIdToNameMapping[matchingCoinItem.strategyId]})`,
                  balance: '-',
                  curr_funding_bp: '-',
                  curr_funding_apr: '-',
                  seven_day_hist_funding_bp: '-',
                  seven_day_hist_funding_apr: '-',
                  tier: '-',
                  position_limit: matchingCoinItem.positionLimit,
                  open_interest: '-',
                  width: matchingCoinItem.globalSpread,
                  uneven: '-',
                  firmwidePricingConfig: matchingCoinItems[0].firmwidePricingConfig
                })
              }
            }
          })
        setTimestamp(formatToLocalMilliTimestampFromUTCNumber(Date.now()))
      }

      setRows(combined)
    }
  }, [data, error, dataStrategy, errorStrategy, strategyIdToNameMapping])

  useEffect(() => {
    // Ignored IIFE to resize all columns
    void (async function () {
      await apiRef.current.autosizeColumns(AUTOSIZE_OPTIONS)
    })()
  }, [rows])

  return (
    <StackColumnLayout>
      <Stack
        sx={{
          width: 1,
          height: 1,
          padding: 2,
          border: 0,
          gap: 1,
          '& .text.positive': {
            color: 'success.main',
          },
          '& .text.negative': {
            color: 'error.main',
          },
        }}>
        <Stack direction='row' gap={1}>
          <Typography color='secondary'>Portfolio:</Typography>
          <Typography>{portfolioId}</Typography>
          <Divider orientation='vertical' flexItem/>
          <Chip label={timestamp} size='small'/>
          <Box marginX='auto'></Box>
          <Chip
            size='small'
            color={flaskStatus ? 'success' : 'error'}
            icon={flaskStatus ? <PowerIcon/> : <PowerOffIcon/>}
            label={`Risk: ${flaskStatus ? 'Online' : 'Offline'}`}
          />
          {
            isAuthorized
              ? <Chip
                size='small'
                color={gatewayStatus ? 'success' : 'error'}
                icon={gatewayStatus ? <PowerIcon/> : <PowerOffIcon/>}
                label={`Gateway: ${gatewayStatus ? 'Online' : 'Offline'}`}
              />
              : <Chip
                size='small'
                color='error'
                label='Gateway: Access Denied'
              />
          }
        </Stack>

        <DataGridPro
          disableRowSelectionOnClick
          apiRef={apiRef}
          columnGroupingModel={COLUMN_GROUPING_MODEL}
          autosizeOptions={AUTOSIZE_OPTIONS}
          sortModel={gridSortModel}
          onSortModelChange={(model) => {
            setGridSortModel(model)
          }}
          autoHeight={true}
          columnHeaderHeight={40}
          rowHeight={20}
          hideFooter={true}
          getRowId={row => row.coin}
          rows={rows}
          columns={COLUMNS}
          sx={{
            '& .MuiDataGrid-container--top [role=row]': {
              background: 'transparent', // remove inherited container background
            },
            '& .MuiDataGrid-container--bottom [role=row]': {
              background: 'transparent', // remove inherited container background
            },
          }}
        />
      </Stack>
    </StackColumnLayout>
  )
}

export default BalancePlusDataGrid
